import noop from 'lodash/noop';
import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  DetailContext,
  DetailMode,
  EvidenceContext,
  Form,
  FormHandle,
  PromptProvider,
  abortableFetch,
  useCrudSource,
} from '@eas/common-web';
import { TechnicalObject } from '@models';
import { EvidenceApiUrl } from '@enums';
import { Fields } from './technical-object-fields';
import { useManualLoadTechnicalObjectHookedSources } from './technical-object-hooked-sources';

export type TechnicalObjectDetailDialogHandle = {
  getData: () => TechnicalObject | null;
};

export const TechnicalObjectDetailDialog = memo(
  forwardRef(function TechnicalObjectDetailDialog(
    {
      id,
      workOrderId,
      workOrderName,
      isZ105,
    }: {
      id?: string;
      workOrderId?: string;
      workOrderName?: string;
      isZ105?: boolean;
    },
    ref: React.Ref<TechnicalObjectDetailDialogHandle>
  ) {
    const formRef = useRef<FormHandle>(null);
    const source = useCrudSource<TechnicalObject>({
      url: EvidenceApiUrl.TECHNICAL_OBJECTS,
      getItem: (api, itemId) =>
        abortableFetch(`${api}/${itemId}`, {
          method: 'GET',
          headers: new Headers({ 'Content-Type': 'application/json' }),
        }),
    });

    useEffect(() => {
      if (id) {
        source.get(id);
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getData: () => formRef.current?.getFieldValues(),
      }),
      []
    );
    const hookedSources = useManualLoadTechnicalObjectHookedSources(
      id ?? '', // TO id
      workOrderId,
      isZ105
    );

    if (!source.data) {
      return null;
    }

    return (
      <PromptProvider>
        <EvidenceContext.Provider value={{ hookedSources } as any}>
          <DetailContext.Provider
            value={{
              source,
              isExisting: true,
              mode: DetailMode.VIEW,
              setMode: noop,
              showErrorPanel: false,
              setActive: noop,
              refresh: noop,
              refreshAll: noop,
              onPersisted: noop,
              startNew: noop,
              startEditing: noop,
              cancelEditing: noop,
              validate: async () => [],
              save: noop,
              del: noop,
              addRefreshListener: noop,
              removeRefreshListener: noop,
              isLocked: false,
              openExportDialog: noop,
              closeExportDialog: noop,
            }}
          >
            <Form
              ref={formRef}
              initialValues={source.data}
              onSubmit={noop}
              editing={false}
            >
              <Fields
                workOrderName={workOrderName}
                workOrderId={workOrderId}
                inPrompt
                isZ105={isZ105}
              />
            </Form>
          </DetailContext.Provider>
        </EvidenceContext.Provider>
      </PromptProvider>
    );
  })
);
